/* eslint-disable react-refresh/only-export-components */
import { DimeScrollArea, DimeScrollBar, DimeSeparator } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/scroll-area')({
  component: ScrollAreaDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

const TAGS = Array.from({ length: 20 }, (_unused, index) => `v1.2.0-beta.${20 - index}`);
const ARTWORK = Array.from({ length: 5 }, (_unused, index) => `Part SKU-${1000 + index}`);

function ScrollAreaDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Scroll Area</h1>
      <p className="mb-8 text-muted-foreground">
        A styled, cross-browser scroll container with custom scrollbars. Constrain its size and the
        overflow becomes scrollable.
      </p>

      <DocsSection title="Vertical">
        <DimeScrollArea type="always" className="h-72 w-48 rounded-md border">
          <div className="p-4">
            <h4 className="mb-4 text-sm font-medium leading-none text-foreground">Tags</h4>
            {TAGS.map((tag) => (
              <React.Fragment key={tag}>
                <div className="text-sm text-foreground">{tag}</div>
                <DimeSeparator className="my-2" />
              </React.Fragment>
            ))}
          </div>
        </DimeScrollArea>
        <p className="mt-3 text-sm text-muted-foreground">
          A fixed height (e.g. <code className="font-mono">h-72</code>) triggers vertical scrolling
          automatically. Rows are divided with <code className="font-mono">DimeSeparator</code>.
        </p>
      </DocsSection>

      <DocsSection title="Horizontal">
        <DimeScrollArea type="always" className="max-w-xl rounded-md border">
          <div className="flex w-max gap-4 p-4">
            {ARTWORK.map((label) => (
              <figure key={label} className="shrink-0">
                <div className="flex size-36 items-center justify-center overflow-hidden rounded-md bg-muted text-xs text-muted-foreground">
                  {label}
                </div>
                <figcaption className="pt-2 text-xs text-muted-foreground">{label}</figcaption>
              </figure>
            ))}
          </div>
          <DimeScrollBar orientation="horizontal" />
        </DimeScrollArea>
        <p className="mt-3 text-sm text-muted-foreground">
          Add <code className="font-mono">DimeScrollBar orientation="horizontal"</code> as a child
          to enable a horizontal scrollbar.
        </p>
      </DocsSection>
    </div>
  );
}
